import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { dispatch, useSelector } from 'store';

// material-ui
import { Grid, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useTheme } from '@mui/material/styles';

// project imports
import { getFormattedDate, getLocalDateTimeFields } from 'helpers/dates';
import { capitalizeString } from 'helpers/stringFunctions';
import { devConsolelog } from 'helpers/utilityFunctions';
import { rowsPerPagePaginationAlt } from 'store/constant';
import { getMemberRechargeHistory } from 'store/slices/memberDetails';
import MainCard from 'ui-component/cards/MainCard';
import { DASHBOARD_VIEW, VIEW_TYPE_MPD_VIEW } from '../member-priority/constant';
import { rechargeHistoryHeadCells } from './constant';
import useStyles from './styles';

// assets
import EnhancedTableHead from 'views/forms/tables/TableEnhanced';

const RechargeHistoryTable = ({ viewType, organizationCode }) => {
    const theme = useTheme();
    const classes = useStyles();

    const memberDetailsData = useSelector((state) => state?.memberDetails);
    const { data = {}, isRechargeHistoryLoading = false } = memberDetailsData || {};
    const { rechargeHistory = {} } = data || {};
    const {
        rechargeHistoryData = [],
        count = 0,
        next = null,
        previous = null,
        page_size: rowsPerPage = rowsPerPagePaginationAlt,
        currentPage = 0
    } = rechargeHistory || {};

    const getRechargeHistoryData = async () => {
        await dispatch(getMemberRechargeHistory('', organizationCode));
    };

    useEffect(() => {
        if (!organizationCode) return;
        getRechargeHistoryData();
    }, [organizationCode]);

    const handleChangePage = async (event, nextPage) => {
        devConsolelog('event', event, nextPage);
        if (nextPage - currentPage > 0) {
            // next
            await dispatch(getMemberRechargeHistory(next, '', nextPage));
        } else if (nextPage - currentPage < 0) {
            // previous
            await dispatch(getMemberRechargeHistory(previous, '', nextPage));
        }
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} sm={8} md={12} lg={12}>
                <MainCard content={false}>
                    {isRechargeHistoryLoading ? (
                        <Grid container sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Grid>
                    ) : (
                        <TableContainer>
                            <Table sx={{ minWidth: 350 }} aria-label="simple table">
                                <EnhancedTableHead headCells={rechargeHistoryHeadCells} tableCellClass={classes.tableCell} />
                                <TableBody>
                                    {rechargeHistoryData.map((recharge) => {
                                        const {
                                            order_request_id: orderRequestId = '',
                                            transaction_date: transactionDate = '',
                                            amount = 0,
                                            status = '',
                                            error_message: errorMessage = ''
                                        } = recharge || {};

                                        const { hours24: hrsLastAct = '', minutes: minLastAct = '' } =
                                            getLocalDateTimeFields(transactionDate) || {};

                                        return (
                                            <TableRow hover>
                                                <TableCell sx={{ pl: 4, minWidth: '100px' }}>{orderRequestId || '-'}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        pl: 4,
                                                        minWidth: '120px'
                                                    }}
                                                    component="th"
                                                >
                                                    {transactionDate ? (
                                                        <>
                                                            <Typography>{`${getFormattedDate(
                                                                transactionDate,
                                                                'dd-mm-yyyy',
                                                                true
                                                            )}`}</Typography>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{ color: theme.palette.grey[500], fontWeight: 400 }}
                                                            >
                                                                {`${hrsLastAct}:${minLastAct}`}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ pl: 4, minWidth: '140px' }}>{amount || '-'}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        pl: 4,
                                                        minWidth: '140px',
                                                        color:
                                                            status.toLowerCase() === 'success'
                                                                ? theme.palette.success.dark
                                                                : theme.palette.grey[500]
                                                    }}
                                                >
                                                    {capitalizeString(status, ' ')}
                                                </TableCell>
                                                <TableCell sx={{ pl: 4, minWidth: '170px' }}>{errorMessage || '-'}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <TablePagination
                        rowsPerPageOptions={[rowsPerPage]}
                        component="div"
                        rowsPerPage={rowsPerPage}
                        page={currentPage}
                        count={parseInt(count, 10)}
                        backIconButtonProps={{
                            'aria-label': 'previous page'
                        }}
                        nextIconButtonProps={{
                            'aria-label': 'next page'
                        }}
                        onPageChange={handleChangePage}
                        labelDisplayedRows={({ from, to, count }) =>
                            `page ${currentPage + 1} of ${Math.ceil(parseInt(count, 10) / rowsPerPage)}`
                        }
                        sx={{ '& .MuiTablePagination-toolbar': { padding: 0 } }}
                    />
                </MainCard>
            </Grid>
        </Grid>
    );
};

RechargeHistoryTable.propTypes = {
    viewType: PropTypes.string,
    organizationCode: PropTypes.string
};

export default RechargeHistoryTable;
